/* eslint-disable @typescript-eslint/no-restricted-imports -- This file defines the reexports */
import React, { useMemo } from 'react';
import type { Property } from 'csstype';
import {
  createTheme,
  darken,
  ThemeProvider as MuiThemeProvider,
  type ThemeProviderProps as MuiThemeProviderProps,
  makeStyles,
  StylesProvider as MuiStylesProvider,
  createGenerateClassName as createMuiGenerateClassName,
  useTheme,
  withStyles,
  type ThemeOptions,
} from '@material-ui/core/styles';
// @ts-expect-error Types are missing from the package, MUI v5/6 shouldn't have this problem
import deepmerge from '@material-ui/utils/deepmerge';
import classNames from 'classnames';
import { adminDarkOptions, adminLightOptions } from '@popmenu/admin-ui';
import { CssBaseline, type Theme } from '@material-ui/core';

import { HostingType, type ButtonStyles, type RootButtonStyles } from '~/libs/gql/types';
import { darkenToReadableColor, readableFontColor } from './colors';
import type { PopmenuTheme } from './withTheme';

export {
  classNames,
  createTheme,
  makeStyles,
  MuiThemeProvider,
  useTheme,
  withStyles,
  type Theme,
};
// Note: v4 has two versions with different type parameter orders,
// this is the props-first one.
export type { ClassNameMap, StyleRules } from '@material-ui/styles';
export type { CreateCSSProperties, CSSProperties, WithStyles } from '@material-ui/core/styles/withStyles';

// default props, using this name simplifies usage
const MuiUseMediaQuery = /* @__PURE__ */ {
  // On the client, read window.matchMedia synchronously to avoid a two-phase render
  // (initial guess → effect correction) that causes DOM elements to detach and re-attach.
  noSsr: true,
  // Simulate an xs-width (0px) viewport for useMediaQuery during SSR.
  // On the server, the noSsr check fails (no window.matchMedia), so this is used instead.
  // MUI breakpoint queries only use min-width/max-width, so a minimal parser suffices.
  ssrMatchMedia: (query: string) => {
    // MUI breakpoint queries always include min-width and/or max-width.
    // At width 0, only min-width:0 (xs) passes; max-width always passes (all breakpoints >= 0).
    const minWidth = query.match(/\(min-width:\s*(\d+(?:\.\d+)?)\s*px\)/)?.[1];
    return { matches: !minWidth || parseFloat(minWidth) <= 0 };
  },
};

export const BASE_Z_INDEX = 1100000;
export const generateClassName = /* @__PURE__ */ createMuiGenerateClassName();
export const generateDarkClassName = /* @__PURE__ */ createMuiGenerateClassName({ seed: 'pm-jss-dark' });

type RestMuiThemeProviderProps = Omit<MuiThemeProviderProps<Theme>, 'theme'>;

interface ThemeProviderProps extends RestMuiThemeProviderProps {
  popmenuTheme?: PopmenuTheme;
  useCssBaseline?: boolean;
}

const buildPopmenuThemeOptions = (popmenuTheme: PopmenuTheme): ThemeOptions => {
  const isEmbed = popmenuTheme.hostingType === HostingType.HostingWidget;
  const textAlign = popmenuTheme.menuItemFontAlignment?.split('_')[2] as Property.TextAlign | undefined;
  const { rootButtonStyles, smallButtonStyles, largeButtonStyles } = popmenuTheme as PopmenuTheme & {
    rootButtonStyles: RootButtonStyles;
    smallButtonStyles: ButtonStyles;
    largeButtonStyles: ButtonStyles;
  };

  const colorDefault = (explicitColor: string | null | undefined, fallback: string) => explicitColor || (isEmbed ? undefined : fallback);

  /* eslint-disable sort-keys */
  return {
    mixins: {
      highZoomMediaQuery: '@media screen and (min-height: 600px)',
    },
    overrides: {
      MuiInputBase: {
        input: {
          '&::placeholder': {
            opacity: '.75',
          },
        },
      },
      MuiFormLabel: {
        root: {
          color: 'rgba(0,0,0,.65)',
        },
      },
      MuiButton: {
        contained: {
          // Apply themed styles inside pm-themed-area, but not inside pm-no-theme
          '.pm-themed-area &:not(.pm-no-theme):not(.pm-no-theme *)': rootButtonStyles,
        },
        containedPrimary: {
          '.pm-themed-area &:not(.pm-no-theme):not(.pm-no-theme *):hover': {
            backgroundColor: darken(popmenuTheme.primaryColor || '#000000', 0.2),
            color: readableFontColor(darken(popmenuTheme.primaryColor || '#000000', 0.2)),
          },
        },
        containedSizeSmall: {
          '.pm-themed-area &:not(.pm-no-theme):not(.pm-no-theme *)': smallButtonStyles,
        },
        containedSizeLarge: {
          '.pm-themed-area &:not(.pm-no-theme):not(.pm-no-theme *)': largeButtonStyles,
        },
        outlined: {
          '.pm-themed-area &:not(.pm-no-theme):not(.pm-no-theme *)': rootButtonStyles,
        },
        outlinedSizeSmall: {
          '.pm-themed-area &:not(.pm-no-theme):not(.pm-no-theme *)': smallButtonStyles,
        },
        outlinedSizeLarge: {
          '.pm-themed-area &:not(.pm-no-theme):not(.pm-no-theme *)': largeButtonStyles,
        },
      },
      MuiOutlinedInput: {
        root: {
          '& fieldset': { border: '1px solid rgba(0, 0, 0, 0.5)' },
        },
      },
      MuiRadio: {
        root: {
          '&.Mui-focusVisible': {
            outline: '2px solid',
          },
        },
      },
    },
    // OurThemeOptions in ~/@types/material-ui.d.ts are based on this, not vice versa.
    // Feel free to change that type if the properties here change.
    consumer: {
      followPrompt: {
        backgroundColor: popmenuTheme.primaryColor || '#F04649',
        color: readableFontColor(popmenuTheme.primaryColor || '#F04649'),
      },
      general: {
        errorMessage: {
          color: '#CC0000',
        },
        infoMessage: {
          color: '#0076CB',
        },
        mutedText: {
          // Intentionally barely muted-
          // cannot be any lighter without violating WCAG contrast ratio
          color: 'rgba(0, 0, 0, 0.8)',
        },
        successMessage: {
          color: '#008000',
        },
      },
      giftCards: {
        backgroundColor: '#FFFFFF',
        color: {
          icon: '#EEEEEE',
          primary: '#000000',
          secondary: '#7F7F7F',
        },
      },
      menus: {
        dishCard: {
          description: {
            color: colorDefault(popmenuTheme.menuItemDescriptionFont?.color, '#000000'),
            fontFamily: popmenuTheme.menuItemDescriptionFont?.family ?? undefined,
            textAlign,
          },
          name: {
            color: colorDefault(popmenuTheme.menuItemNameFont?.color, '#000000'),
            fontFamily: popmenuTheme.menuItemNameFont?.family ?? undefined,
            fontWeight: (popmenuTheme.menuItemNameFont?.weight || 'bold') as Property.FontWeight,
            textAlign,
            textTransform: (popmenuTheme.menuItemNameFont?.case || 'none') as Property.TextTransform,
          },
          featured: {
            backgroundColor: popmenuTheme.featuredDishBgColor || 'rgba(0, 0, 0, 0.05)',
          },
        },
        tabs: {
          borderColor: colorDefault(popmenuTheme.menuHeaderFont?.color, '#000000'),
          color: colorDefault(popmenuTheme.menuHeaderFont?.color, '#000000'),
        },
      },
      menuItemCart: {
        formInfoSubtitle: {
          color: '#9E9E9E',
        },
        errorMessage: {
          color: '#CC0000',
        },
        successMessage: {
          color: '#008000',
        },
        summary: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #F4F4F4',
          color: {
            icon: '#EEEEEE',
            primary: '#000000',
            secondary: '#7F7F7F',
          },
          disclaimer: {
            color: '#CC0000',
          },
          header: {
            backgroundColor: '#F4F4F4',
          },
          link: {
            color: '#0A4F8A',
          },
        },
      },
      nav: {
        rootAnnouncement: {
          backgroundColor: popmenuTheme.primaryColor || '#F04649',
          color: readableFontColor(popmenuTheme.primaryColor || '#F04649'),
        },
      },
      primaryColorOnWhite: darkenToReadableColor('#FFFFFF', popmenuTheme.primaryColor || '#000000'),
      textBoxSection: {
        fontColor: popmenuTheme.customSectionContentFont?.color ?? undefined,
      },
      useLegacyDishLayout: popmenuTheme.useLegacyDishLayout,
    },
    palette: {
      // Raise threshold to 4.5 (WCAG AA) so getContrastText picks the most-readable
      // of black/white for normal-sized text, matching readableFontColor's behavior.
      // MUI's default of 3.0 only satisfies the large-text exception.
      contrastThreshold: 4.5,
      primary: {
        main: popmenuTheme.primaryColor || '#000000',
      },
      secondary: {
        main: popmenuTheme.secondaryColor || '#000000',
      },
      surface: {
        dark: '#ECF0F1',
        main: '#ECF0F1',
        light: '#FFFFFF',
      },
      type: popmenuTheme.paletteType === 'dark_palette_type' ? 'dark' : 'light',
    },
    typography: {
      fontFamily: isEmbed ? 'inherit' : [
        popmenuTheme.defaultFont?.family && `"${popmenuTheme.defaultFont.family}"`,
        '"futura-pt"',
        '"Proxima-Nova"',
        'Arial',
        '"sans-serif"',
      ].filter(Boolean).join(','),
    },
    props: {
      MuiButton: {
        disableFocusRipple: true,
      },
      MuiUseMediaQuery,
    },
  };
  /* eslint-enable sort-keys */
};

// Build material-ui theme from provided Theme model or hardcoded AdminApp options
export const ThemeProvider = ({ popmenuTheme, useCssBaseline = false, ...muiProps }: ThemeProviderProps) => {
  const theme: Theme = useMemo(() => {
    let additionalOptions: ThemeOptions;
    if (popmenuTheme) {
      additionalOptions = buildPopmenuThemeOptions(popmenuTheme);
    } else {
      additionalOptions = {
        palette: {
          background: {
            default: adminLightOptions.palette.surface.main,
          },
        },
        props: {
          MuiUseMediaQuery,
        },
      };
    }
    const merged: ThemeOptions = deepmerge(adminLightOptions, additionalOptions);
    // Replace (not deep-merge) palette overrides so MUI's createPalette
    // regenerates light/dark/contrastText from the new provided properties.
    if (additionalOptions.palette) {
      for (const [key, value] of Object.entries(additionalOptions.palette)) {
        (merged.palette as Record<string, unknown>)[key] = value;
      }
    }

    return createTheme(merged);
  }, [popmenuTheme]);

  return (
    <MuiStylesProvider generateClassName={generateClassName}>
      <MuiThemeProvider
        {...muiProps}
        theme={theme}
      />
      {useCssBaseline && <CssBaseline />}
    </MuiStylesProvider>
  );
};

export const DarkThemeProvider = (muiProps: RestMuiThemeProviderProps) => {
  const theme = useMemo(() => createTheme({
    ...adminDarkOptions,
    // @ts-expect-error TODO: sc-57827 fix in @popmenu/admin-ui
    palette: {
      ...adminDarkOptions.palette,
      background: {
        default: adminDarkOptions.palette.surface.main,
      },
      error: {
        main: adminDarkOptions.palette.error.light,
      },
    },
    // @ts-expect-error TODO: sc-57827 fix in @popmenu/admin-ui
    props: {
      ...adminDarkOptions.props,
      MuiUseMediaQuery,
    },
  }), []);
  return (
    <MuiStylesProvider generateClassName={generateDarkClassName}>
      <MuiThemeProvider {...muiProps} theme={theme} />
    </MuiStylesProvider>
  );
};
